import { useEffect, useRef, useState } from "react";
import { Loader2, CreditCard, Phone } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { DISPATCH_PHONE_DISPLAY, DISPATCH_TEL_HREF } from "@/lib/contact";

const SQ_SDK_URL = "https://web.squarecdn.com/v1/square.js";
const SQ_SDK_TIMEOUT_MS = 10_000;

function loadSquareSdk() {
  if (window.Square) return Promise.resolve(window.Square);
  return new Promise((resolve, reject) => {
    let timeoutId;
    const done = (val, err) => {
      if (timeoutId) clearTimeout(timeoutId);
      err ? reject(err) : resolve(val);
    };
    timeoutId = setTimeout(
      () => done(null, new Error("Square SDK load timed out")),
      SQ_SDK_TIMEOUT_MS,
    );
    const existing = document.querySelector(`script[src="${SQ_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () =>
        done(window.Square || null, window.Square ? null : new Error("Square SDK loaded but global missing")),
      );
      existing.addEventListener("error", () => done(null, new Error("Square SDK failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.src = SQ_SDK_URL;
    s.async = true;
    s.onload = () =>
      done(window.Square || null, window.Square ? null : new Error("Square SDK loaded but global missing"));
    s.onerror = () => done(null, new Error("Square SDK failed to load"));
    document.head.appendChild(s);
  });
}

// Phase 16f — Square Web Payments SDK v1.84 does NOT allow styling the
// iframe input background. Any `backgroundColor` key gets rejected with
// `Invalid style property 'backgroundColor'`. To keep card text visible
// (Bug 1 was white text on Square's default white bg), we now render
// DARK text on Square's default white iframe, and surround the whole
// form with a light card panel so it reads as an intentional "payment
// surface" on the obsidian-black site.
const CARD_STYLE_PRIMARY = {
  ".input-container": {
    borderColor: "#C5A24A",
    borderRadius: "6px",
  },
  ".input-container.is-focus": { borderColor: "#C5A24A" },
  ".input-container.is-error": { borderColor: "#FF6B6B" },
  input: {
    color: "#111111",
    fontFamily: "Helvetica Neue",
    fontSize: "16px",
    fontWeight: "400",
  },
  "input.is-error": { color: "#FF6B6B" },
  "input::placeholder": { color: "#8E9AA6" },
  ".message-text": { color: "#8E9AA6" },
  ".message-icon": { color: "#C5A24A" },
  ".message-text.is-error": { color: "#FF6B6B" },
  ".message-icon.is-error": { color: "#FF6B6B" },
};

// Minimal safe subset — only properties Square v1 is documented to accept.
const CARD_STYLE_FALLBACK = {
  ".input-container": { borderColor: "#C5A24A", borderRadius: "6px" },
  input: { color: "#111111", fontSize: "16px" },
  "input::placeholder": { color: "#8E9AA6" },
};

async function attachCardWithStyleFallbacks(payments, containerSelector) {
  const attempts = [
    { label: "primary", style: CARD_STYLE_PRIMARY },
    { label: "minimal", style: CARD_STYLE_FALLBACK },
    { label: "defaults", style: undefined },
  ];
  let lastErr;
  for (const attempt of attempts) {
    let card;
    try {
      card = attempt.style
        ? await payments.card({ style: attempt.style })
        : await payments.card();
      await card.attach(containerSelector);
      if (attempt.label !== "primary") {
        console.warn(
          `[SQUARE_STYLE] attach succeeded with fallback "${attempt.label}" after previous rejection.`,
        );
      }
      return card;
    } catch (styleErr) {
      lastErr = styleErr;
      console.warn(
        `[SQUARE_STYLE] payments.card()/attach rejected style "${attempt.label}":`,
        styleErr?.message || styleErr,
      );
      try { card?.destroy?.(); } catch { /* ignore */ }
    }
  }
  throw lastErr || new Error("Square card form could not initialise.");
}

// Map internal error_code → which sub-field (if any) the customer needs to fix.
const FIELD_HINT_BY_ERROR_CODE = {
  CVV_FAILURE: "cvv",
  CARD_EXPIRED: "expiry",
  INVALID_EXPIRATION: "expiry",
  AVS_FAILURE: "postal",
  CARD_INVALID: "number",
  INVALID_CARD_NUMBER: "number",
  TOKENIZATION_FAILED: "number",
};

const FIELD_HINT_LABEL = {
  cvv: "↑ Please re-enter the security code (CVV).",
  expiry: "↑ Please check the card expiration date.",
  postal: "↑ Please check the billing ZIP / postal code.",
  number: "↑ Please re-enter the card number.",
};

/**
 * Square Web Payments card form.
 * Props: bookingId, totalPrice (USD), buyerEmail, onSuccess({payment_id, receipt_url}),
 *        onDurationExceeded({phone, message}), onExpired().
 */
export default function SquarePaymentForm({
  bookingId,
  totalPrice,
  buyerEmail,
  manageToken,
  onSuccess,
  onDurationExceeded,
  onExpired,
}) {
  const cardRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [fieldHint, setFieldHint] = useState("");
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: cfg }, Square] = await Promise.all([
          api.get("/payments/config"),
          loadSquareSdk(),
        ]);
        if (cancelled) return;
        const payments = Square.payments(cfg.application_id, cfg.location_id);
        const card = await attachCardWithStyleFallbacks(payments, "#sq-card-container");
        if (cancelled) {
          try { card?.destroy?.(); } catch { /* ignore */ }
          return;
        }
        cardRef.current = card;
        setReady(true);
      } catch (e) {
        console.warn("[SQUARE_INIT] fatal — card form unavailable:", e?.message || e);
        if (!cancelled) setUnavailable(true);
      }
    })();
    return () => {
      cancelled = true;
      try { cardRef.current?.destroy?.(); } catch { /* ignore */ }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cardRef.current || submitting) return;
    setErr("");
    setFieldHint("");
    setSubmitting(true);
    try {
      const result = await cardRef.current.tokenize();
      if (result.status !== "OK") {
        const msg = result.errors?.[0]?.message || "Card details invalid.";
        setErr(msg);
        setFieldHint(FIELD_HINT_LABEL.number);
        setSubmitting(false);
        return;
      }
      const { data } = await api.post("/payments/checkout", {
        booking_id: bookingId,
        source_id: result.token,
        amount_cents: Math.round(totalPrice * 100),
        buyer_email: buyerEmail || undefined,
        manage_token: manageToken || undefined,
      });
      onSuccess?.(data);
    } catch (ex) {
      const status = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      const code = detail?.error_code;
      if (status === 409 && code === "DURATION_EXCEEDED") {
        onDurationExceeded?.({ phone: detail.phone, message: detail.message });
      } else if (status === 410 && code === "BOOKING_EXPIRED") {
        onExpired?.();
      } else {
        // Prefer the structured message from the backend so the customer sees
        // WHY their card failed (decline vs CVV vs AVS vs config).
        const message = typeof detail === "string"
          ? detail
          : (detail?.message || formatApiError(ex));
        setErr(message);
        const hintKey = FIELD_HINT_BY_ERROR_CODE[code];
        if (hintKey) setFieldHint(FIELD_HINT_LABEL[hintKey]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (unavailable) {
    return (
      <div
        data-testid="square-checkout-unavailable"
        className="border border-[#C5A24A]/25 bg-[#111111] px-6 py-6 text-[#E6E6E6] space-y-3"
      >
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#8E9AA6]">
          Payment Form Unavailable
        </div>
        <p className="text-sm leading-relaxed">
          Our secure card form failed to load. Please call{" "}
          <a href={DISPATCH_TEL_HREF} className="underline hover:text-white">
            {DISPATCH_PHONE_DISPLAY}
          </a>{" "}
          and our dispatcher will complete your booking directly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="square-checkout-form" className="space-y-6">
      <div>
        <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
          Payment Method · Square
        </label>
        {/* Light card panel that houses Square's white iframes on the
            obsidian-black site. Square v1 does NOT allow styling the iframe
            background, so we frame it with a gold hairline + light bezel and
            keep DARK text inside the inputs (see CARD_STYLE_PRIMARY). */}
        <div
          id="sq-card-container"
          data-testid="square-card-container"
          className="px-4 py-4 rounded-md bg-white border border-[#C5A24A]/50 min-h-[88px]"
        />
        {fieldHint && (
          <div
            data-testid="square-field-hint"
            className="mt-2 text-xs text-[#FF6B6B] tracking-wide"
          >
            {fieldHint}
          </div>
        )}
        <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6] flex items-center gap-2">
          <CreditCard size={12} strokeWidth={1.5} /> Card charged at confirmation · Receipt emailed
        </div>
      </div>
      {err && (
        <div
          data-testid="square-checkout-error"
          role="alert"
          className="text-sm text-[#FF6B6B] tracking-wide leading-relaxed border border-[#FF6B6B]/40 bg-[#FF6B6B]/5 px-4 py-3 rounded-sm"
        >
          {err}
        </div>
      )}
      <button
        type="submit"
        disabled={submitting || !ready}
        data-testid="square-checkout-confirm-btn"
        className="w-full inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-50 px-8 py-4 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
      >
        {submitting ? (<><Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> Processing…</>)
                    : ready ? (<>Confirm Booking · ${totalPrice?.toFixed(2)}</>)
                    : (<>Initialising secure card form…</>)}
      </button>
      <a
        href={DISPATCH_TEL_HREF}
        data-testid="square-help-phone"
        className="mt-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6] hover:text-white"
      >
        <Phone size={11} strokeWidth={1.5} /> Need help? Call dispatch {DISPATCH_PHONE_DISPLAY}
      </a>
    </form>
  );
}
